// Документът от реда на складовата карта: клик на „Доставка №25" / „Отчет №4"
// отваря СЪЩЕСТВУВАЩИЯ модал на документа през hook-а на модула му (доставка,
// изписване/бракуване, инвентаризация, прехвърляне); действията Анулирай /
// Редактирай са както от реда в справката му — анулирането минава през
// `invalidateAll` и картата се опреснява сама. Разнасянето няма такъв hook:
// модалът му иска записа от списъка, затова той се чете за деня на документа
// (само преглед — корекцията живее в „Разнесени отчети").
import { useState } from "react";
import { App } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getWriteoff, type ReportPosting } from "../../../../api/inventory";
import { getPostedReports } from "../../../../api/inventoryPostings";
import { getTransfer } from "../../../../api/inventoryTransfers";
import type { StockCardDocumentType } from "../../../../api/inventoryStockCard";
import { useDeliveryDocument } from "../../delivery/components/useDeliveryDocument";
import { useWriteoffDocument } from "../../writeoff/components/useWriteoffDocument";
import { useStocktakeDocument } from "../../stocktake/components/useStocktakeDocument";
import { useTransferDocument } from "../../transfer/components/useTransferDocument";
import { ReportPostingDocumentModal } from "../../posting/components/ReportPostingDocumentModal";

export type StockCardDocumentRef = {
  type: StockCardDocumentType;
  id: number;
  date: string; // датата на документа — по нея се намира разнасянето
};

export const useStockCardDocuments = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const delivery = useDeliveryDocument();
  const writeoff = useWriteoffDocument();
  const stocktake = useStocktakeDocument();
  const transfer = useTransferDocument();
  const [posting, setPosting] = useState<ReportPosting | null>(null);

  const failed = () => message.error(t("inventoryPage.errLoad"));

  const openReportPosting = async (id: number, date: string) => {
    const day = dayjs(date);
    const postings = await getPostedReports({
      startDate: day.startOf("day").toISOString(),
      endDate: day.endOf("day").toISOString(),
      status: "all",
    });
    const found = postings.find((item) => item.id === id);
    if (found) setPosting(found);
    else failed();
  };

  const openDocument = (target: StockCardDocumentRef) => {
    const run = async () => {
      switch (target.type) {
        case "delivery":
          return delivery.openById(target.id);
        case "stocktake":
          return stocktake.openById(target.id);
        case "writeoff":
        case "scrap":
          return writeoff.open(await getWriteoff(target.id));
        case "transfer":
          return transfer.open(await getTransfer(target.id));
        case "report":
          return openReportPosting(target.id, target.date);
        case "substitution":
          return undefined; // заварена замяна без родител — няма модал
      }
    };
    run().catch(failed);
  };

  const modals = (
    <>
      {delivery.modals}
      {writeoff.modals}
      {stocktake.modals}
      {transfer.modals}
      <ReportPostingDocumentModal
        open={!!posting}
        posting={posting}
        onClose={() => setPosting(null)}
      />
    </>
  );

  return { openDocument, modals };
};
